import React, { useMemo, useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { getLocalStorage } from '../../lib/get-local-storage'

import styles from './styles/SubscriptionBar.css'
import { PLANS, SUBSCRIPTION_STATUS } from '../../../server/lib/constants'
import * as analytics from './../../lib/analytics'
import { reenableSubscription } from '../../store/ui/actions'

const STORAGE_KEY = 'subchip_v1'

const defaultChip = { trial: false, acknowledged: false }

const storageKeyFor = user => (user?.email ? `${STORAGE_KEY}-${user.email.trim().toLowerCase()}` : STORAGE_KEY)

const readChip = key => {
  const ls = getLocalStorage()
  try {
    const payload = ls.getItem(key)
    if (!payload) return { ...defaultChip }
    const parsed = JSON.parse(payload)
    return {
      trial: !!parsed?.trial,
      acknowledged: !!parsed?.acknowledged
    }
  } catch {
    return { ...defaultChip }
  }
}

const writeChip = (chip, key) => {
  const ls = getLocalStorage()
  try {
    ls.setItem(key, JSON.stringify(chip))
  } catch {}
}

const SubscriptionBannerLoaded = ({ user, chip, write }) => {
  const dispatch = useDispatch()

  const dismiss = useCallback(() => {
    const next = { ...(chip || defaultChip), acknowledged: true }
    write(next)

    analytics.event({
      category: 'Auto-renew banner',
      action: 'Dismiss',
      label: chip?.trial ? 'Trial' : 'Subscription'
    })
  }, [chip, write])

  const style = useMemo(() => {
    const date = user?.subscription?.renewDate
    if (!date) return styles.alert

    const expiry = new Date(date).getTime()
    const diffDays = (expiry - Date.now()) / (1000 * 60 * 60 * 24)

    if (diffDays <= 1) return styles.danger
    if (diffDays <= 15) return styles.warning

    return styles.alert
  }, [user?.subscription?.renewDate])

  const dateInfo = useMemo(() => {
    const date = user?.subscription?.renewDate
    // if there isnt one, or it's in the past, don't show anything
    if (!date || new Date(date).getTime() < Date.now()) return ''
    return `on ${new Date(date).toLocaleDateString()}`
  }, [user?.subscription?.renewDate])

  const textNoun = chip.trial ? 'trial' : 'subscription'

  const handleTurnBackOn = async e => {
    e.preventDefault()
    dispatch(reenableSubscription())
  }

  return (
    <div className={classNames(styles.subscriptionBar, style)}>
      <img
        className={styles.icon}
        src={`${CONFIG.assetsBaseURL}/images/icons/triangle-exclamation-outline.svg`}
        alt='Attention'
      />
      <div className={styles.text}>
        <p>
          You've canceled your auto-renew. Your {textNoun} will end {dateInfo} and you'll lose access to your premium
          features.
        </p>
        <button
          type='button'
          className={styles.buttonLink}
          onClick={handleTurnBackOn}
          aria-label='Turn auto-renew back on'
        >
          Turn auto-renew back on.
        </button>
      </div>
      <button type='button' className={styles.closeButton} onClick={dismiss} aria-label='Dismiss subscription notice'>
        <img
          className={styles.closeIcon}
          src={`${CONFIG.assetsBaseURL}/images/icons/cross.svg`}
          alt=''
          aria-hidden='true'
        />
      </button>
    </div>
  )
}

const SubscriptionBanner = () => {
  const user = useSelector(({ ui }) => ui.user)
  const storageKey = storageKeyFor(user)
  const [chip, setChip] = useState(() => readChip(storageKey))
  const [shouldShow, setShouldShow] = useState(false)

  const write = useCallback(
    next => {
      setChip(next)
      writeChip(next, storageKey)
      setShouldShow(false)
    },
    [storageKey]
  )

  useEffect(() => {
    if (!user || !user.subscription || user?.organizationLicense) {
      setShouldShow(false)
      return
    }

    const sub = user.subscription

    // if the user is active or in trial, never show
    if ([SUBSCRIPTION_STATUS.trialing, SUBSCRIPTION_STATUS.active].includes(sub?.status)) {
      write({
        trial: sub?.status === SUBSCRIPTION_STATUS.trialing,
        acknowledged: false
      })
      return
    }

    const prev = readChip(storageKey)
    setChip(prev)

    const show =
      // `premium` with `inactive` is the state that stripe leaves them in
      sub?.plan === PLANS.premium &&
      sub?.status === SUBSCRIPTION_STATUS.inactive && // but the status is inactive
      !prev.acknowledged // and it hasn't been dismissed

    if (show) {
      analytics.event({
        category: 'Auto-renew banner',
        action: 'Show',
        label: prev.trial ? 'Trial' : 'Subscription'
      })
    }

    setShouldShow(show)
  }, [user?.subscription, storageKey])

  if (!shouldShow) return null
  return <SubscriptionBannerLoaded user={user} chip={chip} write={write} />
}

export default SubscriptionBanner
